import * as Sentry from "@sentry/react";

export const getErrorMessage = (error: unknown, fallback = "An unexpected error occurred"): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  if (typeof error === "string" && error.trim().length > 0) {
    return error;
  }

  if (error && typeof error === "object" && "message" in error && typeof (error as { message?: unknown }).message === "string") {
    return (error as { message: string }).message;
  }

  return fallback;
};

type LogLevel = "error" | "warn" | "info";

interface LogContext {
  feature?: string;
  action?: string;
  [key: string]: unknown;
}

const emitConsoleLog = (level: LogLevel, message: string, payload: Record<string, unknown>) => {
  const entry = {
    level,
    message,
    ...payload,
  };

  let method: "error" | "warn" | "log" = "log";
  if (level === "error") {
    method = "error";
  } else if (level === "warn") {
    method = "warn";
  }

  console[method]("[StoryGauge]", entry);
};

const maybeReportToSentry = (error: unknown, context: LogContext | undefined) => {
  if (Sentry.getClient()) {
    return;
  }
  if (typeof window === "undefined") {
    return;
  }

  const sentry = window.Sentry;
  if (!sentry) {
    return;
  }

  if (typeof sentry.captureException === "function") {
    if (typeof sentry.withScope === "function" && context) {
      sentry.withScope((scope: { setContext: (name: string, details: unknown) => void }) => {
        scope.setContext("story-gauge", context);
        sentry.captureException(error);
      });
      return;
    }
    sentry.captureException(error);
    return;
  }

  if (typeof sentry.captureMessage === "function") {
    sentry.captureMessage(getErrorMessage(error));
  }
};

export const logError = (error: unknown, context?: LogContext) => {
  const message = getErrorMessage(error);
  const payload = {
    context: context ?? {},
    timestamp: new Date().toISOString(),
  };

  emitConsoleLog("error", message, payload);
  maybeReportToSentry(error, context);
  Sentry.captureException(error, (scope) => {
    if (context) {
      scope.setContext("story-gauge", context);
    }
    return scope;
  });

  return message;
};
